import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import { editorTheme } from '../../theme';
import {
  buildQrContent,
  buildQrPath,
  QR_TYPES,
  QrInput,
  QrType,
} from '../../utils/qrCode';
import { QrSettings } from '../../lib/FabricJs/utils/qrObject';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
`;

const TypeButton = styled.button<{ selected: boolean }>`
  height: 34px;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid
    ${({ selected }) => (selected ? editorTheme.accent : editorTheme.border)};
  background: ${({ selected }) =>
    selected ? editorTheme.accentSoft : editorTheme.surface};
  color: ${({ selected }) =>
    selected ? editorTheme.accentHover : editorTheme.text};
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: ${editorTheme.textMuted};
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${editorTheme.border};
  border-radius: 6px;
  font-size: 13px;
  color: ${editorTheme.text};
  &:focus {
    outline: none;
    border-color: ${editorTheme.accent};
  }
`;

const TextArea = styled.textarea`
  min-height: 60px;
  padding: 8px 10px;
  border: 1px solid ${editorTheme.border};
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: ${editorTheme.accent};
  }
`;

const Result = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const PreviewBox = styled.div`
  width: 88px;
  height: 88px;
  flex: none;
  border: 1px solid ${editorTheme.border};
  border-radius: 6px;
  background: ${editorTheme.surfaceMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  & svg {
    width: 100%;
    height: 100%;
  }
`;

const Link = styled.span<{ invalid: boolean }>`
  font-size: 12px;
  line-height: 1.4;
  word-break: break-all;
  color: ${({ invalid }) =>
    invalid ? editorTheme.danger : editorTheme.textMuted};
`;

const Check = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${editorTheme.text};
  & input {
    accent-color: ${editorTheme.accent};
    margin: 0;
  }
`;

const Submit = styled.button`
  height: 40px;
  border-radius: 8px;
  background: ${editorTheme.accent};
  color: white;
  font-size: 13px;
  font-weight: 600;
  &:hover:not(:disabled) {
    background: ${editorTheme.accentHover};
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

interface QrFormProps {
  initial?: Partial<QrSettings>;
  submitLabel: string;
  onSubmit: (settings: QrSettings) => void;
}

const QrForm: FC<QrFormProps> = ({ initial, submitLabel, onSubmit }) => {
  const [type, setType] = useState<QrType>(initial?.type || 'website');
  const [input, setInput] = useState<QrInput>(
    initial?.input || { value: '', message: '' },
  );
  const [background, setBackground] = useState(initial?.background ?? true);

  const definition = QR_TYPES.find((item) => item.type === type) || QR_TYPES[0];
  const content = buildQrContent(type, input);
  const path = useMemo(
    () => (content ? buildQrPath(content) : null),
    [content],
  );

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        if (content) onSubmit({ type, input, content, background });
      }}
    >
      <TypeGrid role="radiogroup" aria-label="QR code type">
        {QR_TYPES.map((item) => (
          <TypeButton
            key={item.type}
            type="button"
            role="radio"
            aria-checked={type === item.type}
            selected={type === item.type}
            onClick={() => setType(item.type)}
          >
            {item.label}
          </TypeButton>
        ))}
      </TypeGrid>
      <Label>
        {definition.field}
        <Input
          value={input.value}
          placeholder={definition.placeholder}
          onChange={(event) =>
            setInput({ ...input, value: event.target.value })
          }
        />
      </Label>
      {type === 'whatsapp' && (
        <Label>
          Message (optional)
          <TextArea
            value={input.message || ''}
            placeholder="Hello! I'd like to know more."
            onChange={(event) =>
              setInput({ ...input, message: event.target.value })
            }
          />
        </Label>
      )}
      <Result>
        <PreviewBox aria-hidden="true">
          {path && (
            <svg viewBox="0 0 100 100">
              {background && <rect width="100" height="100" fill="#ffffff" />}
              <path d={path} fill="#000000" />
            </svg>
          )}
        </PreviewBox>
        <Link invalid={!!input.value.trim() && !content}>
          {content ||
            (input.value.trim()
              ? `This doesn't look like a valid ${definition.label} entry.`
              : 'The QR code will open this link.')}
        </Link>
      </Result>
      <Check>
        <input
          type="checkbox"
          checked={background}
          onChange={(event) => setBackground(event.target.checked)}
        />
        White background (recommended for scanning)
      </Check>
      <Submit type="submit" disabled={!content}>
        {submitLabel}
      </Submit>
    </Form>
  );
};

export default QrForm;
